// Centralized Axios client with auth token interceptor + polling helpers.
import axios from "axios";
import { cacheSet, cacheGet } from "@/utils/offlineCache";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

let accessToken = null;
// Token sessionStorage'da tutulur: tarayıcı kapatıldığında otomatik silinir, açıkken süresiz kalır.
// Eski localStorage anahtarını da temizle (eski oturumlarla tutarlılık için).
try { localStorage.removeItem("hasyapi_token"); } catch { /* ignore */ }
export const setAccessToken = (t) => {
  accessToken = t;
  try {
    if (t) sessionStorage.setItem("hasyapi_token", t);
    else sessionStorage.removeItem("hasyapi_token");
  } catch { /* ignore */ }
};
export const getAccessToken = () => {
  if (accessToken) return accessToken;
  try { return sessionStorage.getItem("hasyapi_token"); }
  catch { return null; }
};

api.interceptors.request.use((cfg) => {
  const t = getAccessToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Sessiz yenileme kilit — aynı anda birden fazla refresh çağrısı yapılmasın.
let refreshPromise = null;
const tryRefresh = async () => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
    .then((r) => { setAccessToken(r.data?.access_token || null); return r.data?.access_token; })
    .catch(() => null)
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

api.interceptors.response.use(
  (r) => {
    // Cache successful GETs for offline fallback
    if (r.config?.method === "get" && r.status >= 200 && r.status < 300) {
      const url = r.config.url;
      if (url && !url.startsWith("/auth/")) cacheSet(url, r.data);
    }
    return r;
  },
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    // 401: refresh token cookie ile sessiz yenileme dene; başarısızsa login'e yönlendir.
    if (err.response?.status === 401 && !url.endsWith("/auth/login") && !url.endsWith("/auth/refresh") && !cfg.__isRetry) {
      const newTok = await tryRefresh();
      if (newTok) {
        cfg.__isRetry = true;
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${newTok}` };
        return api.request(cfg);
      }
      setAccessToken(null);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // Offline / network error → try cache
    const isNetworkErr = !err.response;
    if (isNetworkErr && cfg.method === "get") {
      const cached = cacheGet(cfg.url);
      if (cached) {
        return Promise.resolve({
          status: 200,
          data: cached.data,
          headers: { "x-hasyapi-cache": cached.cachedAt.toISOString() },
          config: cfg,
          fromCache: true,
        });
      }
    }
    return Promise.reject(err);
  }
);

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Beklenmeyen hata";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(d);
};
